import React, { useEffect, useRef } from 'react';
import '../styles/ImageModal.css';

const ImageModal = ({ isOpen, imageSrc, imageAlt, onClose }) => {
  const scrollPositionRef = useRef(0);
  const isClosingRef = useRef(false);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen && !isClosingRef.current) {
      // Store current scroll position
      scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
      
      document.addEventListener('keydown', handleEscape);
      
      // Use a more reliable approach for preventing scroll
      // Add a class instead of directly manipulating styles
      document.body.classList.add('modal-open');
      
      // Store scroll position as CSS variable for mobile
      document.documentElement.style.setProperty('--scroll-y', `${scrollPositionRef.current}px`);
    }

    return () => {
      if (isOpen && !isClosingRef.current) {
        isClosingRef.current = true;
        document.removeEventListener('keydown', handleEscape);
        
        // Remove the modal-open class
        document.body.classList.remove('modal-open');
        
        // Clean up CSS variable
        document.documentElement.style.removeProperty('--scroll-y');
        
        // Restore the scroll position with a small delay to ensure proper cleanup
        if (scrollPositionRef.current > 0) {
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollPositionRef.current);
            isClosingRef.current = false;
          });
        } else {
          isClosingRef.current = false;
        }
      }
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on the image
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageClick = (e) => {
    // Close the modal when clicking the image
    e.stopPropagation();
    onClose();
  };

  // Handle touch events for mobile
  const handleTouchStart = (e) => {
    // Prevent default to avoid unwanted behaviors on mobile
    if (e.target === e.currentTarget) {
      e.preventDefault();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="image-modal-overlay" 
      onClick={handleOverlayClick}
      onTouchStart={handleTouchStart}
    >
      <div className="image-modal-container">
        <button className="image-modal-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img 
          src={imageSrc} 
          alt={imageAlt} 
          className="image-modal-image"
          onClick={handleImageClick}
          draggable={false} /* Prevent image dragging on mobile */
        />
      </div>
    </div>
  );
};

export default ImageModal; 